import * as React from 'react';
import { InputGroup, Input, FormText } from 'reactstrap';
import AutoSuggestContext, { AutoSuggestContextValue } from './AutoSuggestContext';
import FieldLabel from '../edit/fields/FieldLabel';
import { ValidationMessages, FieldProps, PrefixSuffixFieldProps } from '../edit/fields';
import { FieldRenderProps } from 'react-final-form';
import AutoSuggestInputActions from './AutoSuggestInputActions';
import { itemToString } from './Util';
import { InputType } from 'reactstrap/lib/Input';
import { toKebabCase } from 'util/StringUtil';
import classnames from 'classnames';

export type AutoSuggestFieldInputProps = Pick<FieldRenderProps<any, HTMLInputElement>, 'meta'> &
	FieldProps &
	PrefixSuffixFieldProps & { allowNewValues?: boolean; placeholder?: string; inputTypeOverride?: InputType };

/**
 * Renders the input fields for a user to enter values to search within the AutoSuggest.
 *
 * @class AutoSuggestFieldInput
 * @extends {React.Component<AutoSuggestFieldInputProps>}
 */
class AutoSuggestFieldInput extends React.Component<AutoSuggestFieldInputProps> {
	public static contextType: React.Context<any> = AutoSuggestContext;
	public declare context: AutoSuggestContextValue;

	public static defaultProps = {
		indicateDirty: true,
		isRequired: false,
	};

	/**
	 * Display the list of results on focus.
	 *
	 * @memberof AutoSuggestInput
	 */
	public handleOnFocus = () => {
		const {
			autoSuggestOptions: { expandOnClick = true },
			downshift: { toggleMenu },
		} = this.context;

		if (expandOnClick) {
			toggleMenu();
		}
	};

	public render() {
		const {
			meta,
			label,
			name,
			forceEmptyLabel,
			labelElementHidden,
			isRequired,
			disabled,
			readOnly,
			allowNewValues,
			prefix,
			suffix,
			isPowerUser,
			placeholder = `Begin typing to search${allowNewValues != null && allowNewValues ? ' or create new' : ''}`,
			inputTypeOverride,
			testId,
			hideTooltip,
		} = this.props;

		const {
			name: contextName,
			value,
			downshift: { getInputProps, getLabelProps },
		} = this.context;

		const labelProps = getLabelProps();
		const inputProps = getInputProps();

		let readOnlyDisplay;

		if (readOnly) {
			if (Array.isArray(value)) {
				if (value.length === 0) {
					readOnlyDisplay = <em>No values selected.</em>;
				} else {
					readOnlyDisplay = <em>{value.map(itemToString).join(', ')}</em>;
				}
			} else if (value) {
				readOnlyDisplay = <em>{value.label}</em>;
			} else {
				readOnlyDisplay = <em>&lt;Empty&gt;</em>;
			}
		}

		// eslint-disable-next-line no-unused-vars
		const { type, ref, ...autoSuggestInputProps } = getInputProps({
			type: 'text',
			name: contextName,
			'aria-labelledby': `label-${labelProps.id}`,
		});

		const hideClearButton = !value || Array.isArray(value);

		return (
			<>
				<FieldLabel
					htmlFor={inputProps.id}
					label={label}
					isRequired={isRequired}
					id={labelProps.id}
					isPowerUser={isPowerUser}
					forceEmptyLabel={forceEmptyLabel}
					labelElementHidden={labelElementHidden}
					name={name}
					hideTooltip={hideTooltip}
				/>
				{readOnly ? (
					<FormText>{readOnlyDisplay}</FormText>
				) : (
					<>
						<div
							className={classnames('input-wrapper', {
								'input-wrapper--show-clear': !hideClearButton,
							})}
						>
							{prefix ? <div className="input-wrapper__prefix">{prefix}</div> : null}
							<InputGroup
								className={!meta.dirtySinceLastSubmit && meta.submitError ? 'input-group-invalid' : ''}
							>
								<Input
									className="form-control--cancel-next"
									type={inputTypeOverride || 'text'}
									placeholder={placeholder}
									{...autoSuggestInputProps}
									onFocus={this.handleOnFocus}
									invalid={!meta.dirtySinceLastSubmit && meta.submitError ? true : false}
									disabled={disabled}
									data-testid={testId || `field-${toKebabCase(name)}`}
									onKeyPress={(e) => {
										e.key === 'Enter' && e.preventDefault();
									}}
								/>
								<AutoSuggestInputActions disabled={disabled} name={name} />
							</InputGroup>

							{suffix ? <div className="input-wrapper__suffix">{suffix}</div> : null}
						</div>

						<ValidationMessages id={name + '-invalid'} meta={meta} />
					</>
				)}
			</>
		);
	}
}

export default AutoSuggestFieldInput;
